import type FroalaEditor from 'froala-editor';
import { useEffect } from 'react';
import type ReactFroalaEditor from 'react-froala-wysiwyg';

const isAnyInputFocused = () => {
  const activeElement = document.activeElement;

  return (
    activeElement &&
    (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
  );
};

export function useAutoCursorPlacement({
  editorRef,
  cursorRef,
  autoCursorPositionSelector,
  value,
  editor,
}: {
  editorRef: React.RefObject<{ editor: FroalaEditor }>;
  cursorRef: React.RefObject<boolean>;
  autoCursorPositionSelector: string;
  value: string;
  editor: ReactFroalaEditor;
}) {
  useEffect(() => {
    if (
      !cursorRef.current &&
      autoCursorPositionSelector &&
      editorRef.current &&
      !isAnyInputFocused()
    ) {
      const editorInstance = editorRef.current.editor;
      if (!editorInstance) return;

      const $el: HTMLElement | null = editorInstance.$el.get(0);
      if (!$el) return;

      const timeoutId = setTimeout(() => {
        if (isAnyInputFocused() || cursorRef.current) {
          return;
        }

        // Find the marker that separates new email from previous/quoted email
        const nextEmailStartMarker = $el.querySelector(
          'hr[data-next-email-start]'
        );

        const allTargetEls = $el.querySelectorAll(autoCursorPositionSelector);
        let filteredTargetEls: Element[] = [];

        if (nextEmailStartMarker) {
          // Filter to only include elements BEFORE the previous email marker
          // to avoid placing cursor in quoted email content
          for (let i = 0; i < allTargetEls.length; i++) {
            const el = allTargetEls[i];
            const markerPosition =
              nextEmailStartMarker.compareDocumentPosition(el);
            if (markerPosition & Node.DOCUMENT_POSITION_PRECEDING) {
              filteredTargetEls.push(el);
            }
          }
        } else {
          filteredTargetEls = Array.from(allTargetEls);
        }

        const targetEl = filteredTargetEls?.length
          ? filteredTargetEls[filteredTargetEls.length - 1]
          : null;
        if (targetEl) {
          // Check if there's already a suitable element before the target
          // where cursor can be placed
          const prevElement = targetEl.previousElementSibling as HTMLElement;
          let focusTarget: HTMLElement | null = null;

          if (
            prevElement &&
            (prevElement.nodeName === 'P' || prevElement.nodeName === 'DIV')
          ) {
            focusTarget = prevElement;
          } else if (targetEl.previousSibling) {
            // Only create a new paragraph if there's no suitable previous element
            const newParagraph = createNewParagraph();
            $el.insertBefore(newParagraph, targetEl);
            focusTarget = newParagraph;
          }

          if (focusTarget) {
            // Place cursor at the end of the focus target
            const range = document.createRange();
            range.selectNodeContents(focusTarget);
            range.collapse(false);

            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);

            editorInstance.selection.save();
            editorInstance.selection.restore();
          }
        } else {
          // fallback: ensure cursor can be placed at the end
          const lastChild = $el.lastChild;

          if (
            !lastChild ||
            !(
              lastChild.nodeType === 1 &&
              ['P', 'DIV'].includes(lastChild.nodeName)
            )
          ) {
            const newParagraph = createNewParagraph();
            $el.appendChild(newParagraph);
          }

          editorInstance.selection.setAtEnd($el);
          editorInstance.selection.restore();
        }
      }, 1000);
      // TextEditor renders content partially, in a rather disorganised manner,
      // so added a quite safe time delay to place the cursor to the correct
      // position after the render

      // track user interaction to prevent cursor to be set once again at the end
      const handleUserInteraction = () => {
        if (!cursorRef.current) {
          cursorRef.current = true;
        }
      };

      $el.addEventListener('click', handleUserInteraction);
      $el.addEventListener('keydown', handleUserInteraction);
      $el.addEventListener('touchstart', handleUserInteraction);

      return () => {
        clearTimeout(timeoutId);
        $el.removeEventListener('click', handleUserInteraction);
        $el.removeEventListener('keydown', handleUserInteraction);
        $el.removeEventListener('touchstart', handleUserInteraction);
      };
    }
  }, [cursorRef, editorRef, autoCursorPositionSelector, value, editor]);
}

const createNewParagraph = () => {
  const newParagraph = document.createElement('p');
  newParagraph.style.margin = '0';
  newParagraph.innerHTML = '<br>';

  return newParagraph;
};
